import { motion } from "framer-motion";
import { useSettings } from "../contexts/SettingsContext";

export default function HowItWorks() {
  const { settings } = useSettings();
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const lineVariants = {
    hidden: { pathLength: 0, opacity: 0 },
    visible: {
      pathLength: 1,
      opacity: 1,
      transition: {
        duration: 1.5,
        ease: "easeInOut",
        delay: 0.5,
      },
    },
  };

  return (
    <section id="how-it-works" className="py-24 relative overflow-hidden">
      {/* Background decoration */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: "rgba(233, 92, 73, 0.05)" }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="text-center mb-16">
          <motion.h2
            variants={itemVariants}
            className="text-3xl sm:text-4xl lg:text-5xl font-oxanium font-bold mb-6"
            style={{ color: settings.primaryColor }}
          >
            {settings.howItWorksTitle}
          </motion.h2>
          <motion.p
            variants={itemVariants}
            className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto"
          >
            {settings.howItWorksSubtitle}
          </motion.p>
        </header>

        {/* Desktop Layout */}
        <div className="hidden lg:block">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="relative"
          >
            {/* Connection lines */}
            <svg
              className="absolute inset-0 w-full h-full"
              viewBox="0 0 1000 400"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <motion.path
                d="M 250 200 Q 375 100 500 200 Q 625 300 750 200"
                stroke="url(#gradient)"
                strokeWidth="2"
                fill="none"
                variants={lineVariants}
                className="opacity-30"
              />
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#e95c49" />
                  <stop offset="50%" stopColor="#e95c49" />
                  <stop offset="100%" stopColor="#e95c49" />
                </linearGradient>
              </defs>
            </svg>

            <div className="grid grid-cols-4 gap-8 relative z-10">
              {settings.howItWorksSteps.map((step) => {
                const formattedStep = `${String(step.step).padStart(2, "0")}`;
                return (
                  <motion.div
                    key={step.step}
                    variants={itemVariants}
                    className="text-center"
                  >
                    <motion.div
                      className="mb-6 flex justify-center"
                      whileHover={{ scale: 1.05 }}
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 20,
                      }}
                    >
                      {/* Replaced icon & corner badge with centered step number */}
                      <div className="w-20 h-20 rounded-2xl flex items-center justify-center shadow-lg border-2 border-[#e95c49] bg-transparent">
                        <span className="text-2xl font-extrabold tracking-wider text-[#e95c49] dark:text-white">
                          {formattedStep}
                        </span>
                      </div>
                    </motion.div>

                    <h3 className="text-xl font-semibold mb-3 text-gray-900 dark:text-white font-oxanium">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                      {step.description}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>

        {/* Mobile Layout */}
        <div className="lg:hidden">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="space-y-8"
          >
            {settings.howItWorksSteps.map((step, index) => {
              const formattedStep = `${String(step.step).padStart(2, "0")}`;
              return (
                <motion.div
                  key={step.step}
                  variants={itemVariants}
                  className="flex items-start gap-6"
                >
                  <motion.div
                    className="flex-shrink-0 relative"
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  >
                    {/* Replaced icon & corner badge with centered step number */}
                    <div className="w-16 h-16 rounded-xl flex items-center justify-center shadow-lg border-2 border-[#e95c49] bg-transparent">
                      <span className="text-xl font-extrabold tracking-wider text-[#e95c49] dark:text-white">
                        {formattedStep}
                      </span>
                    </div>
                  </motion.div>

                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white font-oxanium">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                      {step.description}
                    </p>
                  </div>

                  {/* Connection line for mobile */}
                  {index < settings.howItWorksSteps.length - 1 && (
                    <motion.div
                      className="absolute left-8 mt-16 w-px h-8 opacity-30"
                      style={{ backgroundColor: "#e95c49" }}
                      initial={{ scaleY: 0 }}
                      whileInView={{ scaleY: 1 }}
                      transition={{ duration: 0.5, delay: index * 0.2 }}
                      viewport={{ once: true }}
                    />
                  )}
                </motion.div>
              );
            })}
          </motion.div>
        </div>

        {/* Video Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-20"
        >
          <div className="text-center mb-8">
            <h3
              className="text-2xl font-oxanium font-bold mb-4"
              style={{ color: "#e95c49" }}
            >
              {settings.howItWorksVideoTitle}
            </h3>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              {settings.howItWorksVideoSubtitle}
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <video
                className="w-full h-auto"
                controls
                preload="metadata"
                poster="/seeinaction.png"
                onError={() => {
                  // Handle video error
                }}
                onLoadStart={() => {
                  // Video loading started
                }}
                onCanPlay={() => {
                  // Video can play
                }}
              >
                <source src="/howitworks.mp4" type="video/mp4" />
                <source src="./howitworks.mp4" type="video/mp4" />
                <p className="text-center p-8 text-gray-600 dark:text-gray-300">
                  Your browser does not support the video tag.
                  <br />
                  <a
                    href="/howitworks.mp4"
                    className="text-primary-500 hover:underline"
                  >
                    Download the video instead
                  </a>
                </p>
              </video>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Floating elements */}
      <motion.div
        className="absolute top-10 left-10 w-24 h-24 rounded-full blur-xl"
        style={{ backgroundColor: "rgba(233, 92, 73, 0.2)" }}
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.2, 0.4, 0.2],
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
    </section>
  );
}
